/* ══════════════════════════════════════════════════════════════
   Search Modal — Universal Search Overlay
   ⌘K / Ctrl+K to open, searches headlines, companies, symbols
   ══════════════════════════════════════════════════════════════ */

export interface SearchResult {
    type: 'company' | 'headline' | 'page' | 'sector';
    title: string;
    subtitle: string;
    url?: string;
    emoji: string;
}

let isOpen = false;
let searchIndex: SearchResult[] = [];

async function buildSearchIndex(): Promise<SearchResult[]> {
    const [{ ROUTES }, { NIFTY50_COMPANIES }, { fetchPriorityHeadlines }] = await Promise.all([
        import('../config/routes'),
        import('../config/india-companies'),
        import('../services/news-feed'),
    ]);

    const pageResults: SearchResult[] = ROUTES.map(route => ({
        type: 'page',
        title: route.label,
        subtitle: `Navigate to ${route.label}`,
        url: `#${route.path}`,
        emoji: route.icon,
    }));

    const companyResults: SearchResult[] = NIFTY50_COMPANIES.slice(0, 50).map(company => ({
        type: 'company',
        title: company.name,
        subtitle: `${company.symbol} · ${company.sector}`,
        url: '#/markets',
        emoji: '🏢',
    }));

    let headlineResults: SearchResult[] = [];
    try {
        const headlines = await fetchPriorityHeadlines();
        headlineResults = headlines.slice(0, 8).map(item => ({
            type: 'headline',
            title: item.title,
            subtitle: `${item.source} · ${item.timeAgo}`,
            url: item.link,
            emoji: '📰',
        }));
    } catch {
        headlineResults = [];
    }

    return [...pageResults, ...companyResults, ...headlineResults];
}

export function initSearchModal(): void {
    // Create modal if not exists
    if (document.getElementById('search-modal')) return;

    const modal = document.createElement('div');
    modal.id = 'search-modal';
    modal.className = 'search-modal-overlay hidden';
    modal.innerHTML = `
    <div class="search-modal glass-card">
      <div class="search-input-wrapper">
        <span class="search-icon">🔍</span>
        <input type="text" id="global-search" placeholder="Search companies, news, pages..." />
        <kbd class="search-kbd">ESC</kbd>
      </div>
      <div class="search-results-container" id="search-results"></div>
      <div class="search-footer"><small>↑↓ Navigate · Enter Select · ESC Close</small></div>
    </div>`;
    document.body.appendChild(modal);

    // ⌘K / Ctrl+K to toggle
    document.addEventListener('keydown', (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
            e.preventDefault();
            toggleSearch();
        }
        if (e.key === 'Escape' && isOpen) closeSearch();
    });

    modal.addEventListener('click', (e) => {
        if (e.target === modal) closeSearch();
    });

    const input = document.getElementById('global-search') as HTMLInputElement;
    input?.addEventListener('input', () => {
        void refreshSearchIndexAndRender(input.value);
    });
}

function toggleSearch(): void {
    isOpen ? closeSearch() : openSearch();
}

function openSearch(): void {
    const modal = document.getElementById('search-modal');
    if (modal) {
        modal.classList.remove('hidden');
        isOpen = true;
        const input = document.getElementById('global-search') as HTMLInputElement;
        input?.focus();
        void refreshSearchIndexAndRender('');
    }
}

function closeSearch(): void {
    const modal = document.getElementById('search-modal');
    if (modal) { modal.classList.add('hidden'); isOpen = false; }
}

function filterResults(query: string): void {
    const resultsDiv = document.getElementById('search-results');
    if (!resultsDiv) return;

    const normalizedQuery = query.toLowerCase();
    const filtered = query
        ? searchIndex.filter(r => r.title.toLowerCase().includes(normalizedQuery) || r.subtitle.toLowerCase().includes(normalizedQuery))
        : searchIndex;

    resultsDiv.innerHTML = filtered.map(r => `
    <a class="search-result-item" href="${r.url || '#'}">
      <span class="result-emoji">${r.emoji}</span>
      <div class="result-info">
        <span class="result-title">${r.title}</span>
        <span class="result-subtitle">${r.subtitle}</span>
      </div>
      <span class="result-type">${r.type}</span>
    </a>`).join('') || '<div class="no-results">No results found</div>';
}

async function refreshSearchIndexAndRender(query: string): Promise<void> {
    if (searchIndex.length === 0) {
        searchIndex = await buildSearchIndex();
    }
    filterResults(query);
}
